import { useRouter } from 'next/router';
import { ReactNode } from 'react';
import { useQuery } from 'react-query';
import { CacheKey } from 'src/constants/cache-key';
import { IS_DEV, IS_PROD } from 'src/constants/env';
import { logger } from 'src/lib/logger';
import type { Feature } from './feature.d';

interface Props extends UseFeatureFlag {
  children: ReactNode;
}

/**
 * @example
 * ```tsx
 * <FeatureFlag
 *    name='your-feature'
 * >
 *  /// your feature component
 * </FeatureFlag>
 * ```
 */
export function FeatureFlag(props: Props): ReactNode {
  const { children, ...opts } = props;
  const [enabled] = useFeatureFlag(opts);
  if (enabled) {
    return props.children;
  }
  return null;
}

interface UseFeatureFlag {
  name: Feature;
  /**
   * Auto enable feature on dev.
   * Use this in caution, you are responsible check the code is working on prod when feature is not active
   */
  forceEnableDev?: boolean;
  /**
   * add query param `?feature=name` will enable feature on production
   */
  enableQueryProd?: boolean;
  defaultShow?: boolean;
}

export function useFeatureFlag({
  name,
  forceEnableDev,
  defaultShow,
  enableQueryProd,
}: UseFeatureFlag) {
  const router = useRouter();
  const featureQuery = router.query.feature;

  const isFeatureQueryExisted =
    typeof featureQuery === 'string' && featureQuery === name;
  const shouldShowOnDev =
    IS_DEV && (!!forceEnableDev || isFeatureQueryExisted || !!defaultShow);
  const shouldShowOnProd = IS_PROD && enableQueryProd && isFeatureQueryExisted;
  const initialData = shouldShowOnDev || shouldShowOnProd || !!defaultShow;

  const query = useQuery(
    [CacheKey.featureFlag, name],
    shouldShowOnDev || shouldShowOnProd
      ? () => Promise.resolve(true)
      : () => fetchFeatureFlag(name),
    {
      refetchOnMount: IS_DEV,
      refetchOnWindowFocus: false,
      initialData,
    }
  );

  const enable = query.data;
  return [enable, query] as const;
}

const fetchFeatureFlag = async (name: string) => {
  let enabled = false;
  try {
    const resp = await fetch(`api/features?flag=${name}`, {
      headers: {
        'content-type': 'application/json',
      },
    });

    const body = (await resp.json()) as { enabled: boolean };

    enabled = body.enabled;
  } catch (error) {
    logger.error(error);
  }

  return enabled;
};
